"use client";

import { useState, useEffect, useCallback } from "react";
import { applyPhoneInput, phoneToE164 } from "@/lib/phoneMask";
import { SmartCaptcha, captchaEnabled } from "../SmartCaptcha";

export type PartnerModalProps = {
  open: boolean;
  onClose: () => void;
  cityNames?: string[];
  routing?: {
    email?: string;
    telegramChatId?: string;
    hashtag?: string;
    emailFrom?: string;
  };
};

/**
 * Модалка «Стать партнёром» — отдельная от «Получить предложение».
 * Поля: Город (select), Имя, Телефон (маска +7), Комментарий.
 * Отправка на /api/lead с partner-routing (source=partner).
 */
export default function PartnerModal({ open, onClose, cityNames, routing }: PartnerModalProps) {
  const cities = cityNames ?? [];
  const [city, setCity] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");

  const close = useCallback(() => {
    setDone(false);
    setError(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError("Заполните имя и телефон");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const composed = [city && `Город: ${city}`, comment && `Комментарий: ${comment}`]
        .filter(Boolean)
        .join("\n");
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          phone: phoneToE164(phone),
          comment: composed,
          source: "partner",
          smartCaptchaToken: captchaToken,
          ...(routing?.email ? { application_email: routing.email } : {}),
          ...(routing?.telegramChatId ? { telegram_chat_id: routing.telegramChatId } : {}),
          ...(routing?.hashtag ? { hashtag: routing.hashtag } : {}),
          ...(routing?.emailFrom ? { email_from: routing.emailFrom } : {}),
        }),
      });
      if (!res.ok) throw new Error("send failed");
      setDone(true);
    } catch {
      setError("Не удалось отправить. Позвоните +7 495 108-48-48.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      onClick={close}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4 py-6 backdrop-blur-[2px]"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[460px] overflow-hidden rounded-[24px] bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.4)]"
      >
        <div className="flex items-start justify-between px-7 pt-7 pb-2 sm:px-8 sm:pt-8">
          <div>
            <h3 className="text-[14px] font-semibold uppercase tracking-[1.5px] text-primary">
              Стать партнёром
            </h3>
            <p className="mt-1.5 text-[13px] text-text-muted">
              Оставьте контакты — менеджер свяжется и расскажет про условия сотрудничества.
            </p>
          </div>
          <button
            onClick={close}
            aria-label="Закрыть"
            className="-mr-2 -mt-1 grid size-9 shrink-0 place-items-center rounded-full text-2xl leading-none text-black/40 transition hover:bg-black/[0.05] hover:text-black"
          >
            ×
          </button>
        </div>

        {done ? (
          <div className="px-7 pb-8 pt-4 text-center sm:px-8">
            <div className="mx-auto mb-4 inline-flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l5 5 9-11" />
              </svg>
            </div>
            <p className="mb-1.5 text-[18px] font-medium text-text">Заявка отправлена</p>
            <p className="text-[13px] text-text-muted">Менеджер свяжется с вами в течение рабочего дня.</p>
            <button
              onClick={close}
              className="mt-5 rounded-[12px] bg-primary px-7 py-3 text-[14px] font-medium uppercase tracking-[1px] text-white hover:bg-primary-hover"
            >
              Закрыть
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-3 px-7 pb-7 pt-3 sm:px-8 sm:pb-8">
            <Select label="Укажите город" value={city} onChange={setCity} options={cities} />
            <TextField label="Ваше имя" value={name} onChange={setName} />
            <PhoneField label="Ваш телефон" value={phone} onChange={setPhone} />
            <label className="flex flex-col gap-1.5">
              <span className="sr-only">Комментарий</span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Комментарий (необязательно)"
                rows={3}
                className="w-full resize-y rounded-[12px] border border-black/10 bg-bg px-4 py-3.5 text-[14px] text-text outline-none transition placeholder:text-text-muted/70 focus:border-primary focus:bg-white"
              />
            </label>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700">{error}</p>
            )}

            <SmartCaptcha onToken={setCaptchaToken} />

            <button
              type="submit"
              disabled={sending || (captchaEnabled && !captchaToken)}
              className="mt-2 rounded-[14px] bg-primary py-4 text-[13px] font-semibold uppercase tracking-[1.5px] text-white transition hover:bg-primary-hover disabled:opacity-60"
            >
              {sending ? "ОТПРАВЛЯЕМ…" : "ОТПРАВИТЬ ЗАЯВКУ"}
            </button>
            <p className="text-center text-[11px] leading-[1.4] text-text-muted/80">
              Нажимая «Отправить», вы соглашаетесь с обработкой персональных данных.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function fieldClass(filled: boolean) {
  return `w-full appearance-none rounded-[12px] border border-black/10 bg-bg px-4 py-3.5 text-[14px] text-text outline-none transition placeholder:text-text-muted/70 focus:border-primary focus:bg-white ${
    filled ? "" : "text-text-muted"
  }`;
}

function Select({
  label, value, onChange, options,
}: { label: string; value: string; onChange: (v: string) => void; options: readonly string[] }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} className={`${fieldClass(!!value)} pr-10`}>
        <option value="">{label}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <svg className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-text-muted" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
  );
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="text"
      placeholder={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={120}
      className={fieldClass(!!value)}
    />
  );
}

function PhoneField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="tel"
      placeholder={label}
      value={value}
      onChange={(e) => onChange(applyPhoneInput(e.target.value, value))}
      onFocus={(e) => { if (!e.target.value) onChange("+7 "); }}
      onBlur={(e) => { if (e.target.value.replace(/\D/g, "") === "7") onChange(""); }}
      maxLength={20}
      className={fieldClass(!!value)}
    />
  );
}
